import { useState, useEffect } from "react";
import { api } from "@/lib/api";
import { queuePreStart } from "@/lib/offlineQueue";
import { toast } from "sonner";
import { Check, X, ClipboardCheck, Camera, Loader2 } from "lucide-react";

const DEFAULT_CHECKLIST = [
  "Oil / Coolant level",
  "Tyres / Tracks",
  "Hydraulics & leaks",
  "Guards / Safety devices",
  "Lights",
  "Brakes / Steering",
  "Warning lights",
];

function resizeToDataUrl(file, maxSize = 1400, quality = 0.8) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const reader = new FileReader();
    reader.onload = (e) => { img.src = e.target.result; };
    reader.onerror = reject;
    img.onload = () => {
      let { width, height } = img;
      if (width > maxSize || height > maxSize) {
        if (width >= height) { height = Math.round((height * maxSize) / width); width = maxSize; }
        else { width = Math.round((width * maxSize) / height); height = maxSize; }
      }
      const canvas = document.createElement("canvas");
      canvas.width = width; canvas.height = height;
      canvas.getContext("2d").drawImage(img, 0, 0, width, height);
      resolve(canvas.toDataURL("image/jpeg", quality));
    };
    img.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export default function PreStartForm({ machineId, onDone, onCancel, defaultOperator = "" }) {
  const [checklist, setChecklist] = useState(DEFAULT_CHECKLIST);
  const [operator, setOperator] = useState(defaultOperator);
  const [hours, setHours] = useState("");
  const [km, setKm] = useState("");
  const [notes, setNotes] = useState("");
  const [statuses, setStatuses] = useState(() => DEFAULT_CHECKLIST.reduce((a, l) => ({ ...a, [l]: "pass" }), {}));
  const [photo, setPhoto] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get(`/public/machines/${machineId}/checklist`).then(({ data }) => {
      const items = data.items && data.items.length ? data.items : DEFAULT_CHECKLIST;
      setChecklist(items);
      setStatuses((prev) => items.reduce((a, l) => ({ ...a, [l]: prev[l] || "pass" }), {}));
    }).catch(() => {});
  }, [machineId]);

  const setItem = (label, status) => setStatuses((s) => ({ ...s, [label]: status }));
  const anyFail = Object.values(statuses).includes("fail");

  const onPhoto = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    try { setPhoto(await resizeToDataUrl(file)); }
    catch { toast.error("Could not process the photo."); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!operator.trim()) { toast.error("Please enter your name."); return; }
    setLoading(true);
    const payload = {
      operator_name: operator.trim(),
      hours: hours ? parseFloat(hours) : null,
      km: km ? parseFloat(km) : null,
      items: checklist.map((l) => ({ label: l, status: statuses[l] || "pass" })),
      notes: notes.trim(),
      photo,
    };
    try {
      const { data } = await api.post(`/public/machines/${machineId}/prestart`, payload);
      onDone(data);
    } catch (err) {
      // No network/response → save offline and let the user carry on.
      if (!navigator.onLine || !err.response) {
        queuePreStart(machineId, payload);
        onDone({ offline: true });
      } else {
        toast.error("Could not submit pre-start. Please try again.");
      }
    } finally { setLoading(false); }
  };

  return (
    <form onSubmit={submit} className="card p-5 md:p-6 space-y-5" data-testid="prestart-form">
      <div className="flex items-center gap-2 border-b border-white/10 pb-3">
        <ClipboardCheck className="w-5 h-5 text-[#FFB800]" />
        <h2 className="font-head font-800 uppercase tracking-wide text-xl">Pre-Start Check</h2>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="sm:col-span-3">
          <label className="il-label">Operator Name *</label>
          <input className="il-input" value={operator} onChange={(e) => setOperator(e.target.value)} required placeholder="Your name" data-testid="prestart-operator" />
        </div>
        <div><label className="il-label">Hours</label><input className="il-input font-mono" type="number" step="0.1" value={hours} onChange={(e) => setHours(e.target.value)} placeholder="1200" data-testid="prestart-hours" /></div>
        <div><label className="il-label">Odometer (km)</label><input className="il-input font-mono" type="number" value={km} onChange={(e) => setKm(e.target.value)} placeholder="85000" data-testid="prestart-km" /></div>
      </div>

      <div className="space-y-2">
        {checklist.map((label, i) => (
          <div key={label} className="flex items-center justify-between border border-white/10 bg-white/[0.02] px-3 py-2.5" data-testid={`prestart-item-${i}`}>
            <span className="font-body text-sm pr-3">{label}</span>
            <div className="flex gap-1.5 shrink-0">
              <button type="button" onClick={() => setItem(label, "pass")}
                className={`flex items-center gap-1 px-3 h-9 font-head text-xs uppercase tracking-wide transition-colors ${statuses[label] === "pass" ? "bg-[#22C55E] text-black" : "border border-white/15 text-[#94A3B8] hover:text-white"}`}
                data-testid={`prestart-pass-${i}`}><Check className="w-3.5 h-3.5" /> Pass</button>
              <button type="button" onClick={() => setItem(label, "fail")}
                className={`flex items-center gap-1 px-3 h-9 font-head text-xs uppercase tracking-wide transition-colors ${statuses[label] === "fail" ? "bg-[#FF3B30] text-white" : "border border-white/15 text-[#94A3B8] hover:text-white"}`}
                data-testid={`prestart-fail-${i}`}><X className="w-3.5 h-3.5" /> Fail</button>
            </div>
          </div>
        ))}
      </div>

      <div>
        <label className="il-label">Notes {anyFail && <span className="text-[#FF3B30] normal-case">— describe the fault</span>}</label>
        <textarea className="il-input py-3" style={{ height: "auto", minHeight: "5rem" }} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Anything the manager should know…" data-testid="prestart-notes" />
      </div>

      <div>
        <label className="inline-flex items-center gap-2 cursor-pointer h-11 px-4 border border-white/15 text-[#94A3B8] hover:border-[#FFB800] hover:text-[#FFB800] transition-colors font-head text-sm" data-testid="prestart-photo-label">
          <Camera className="w-4 h-4" /> {photo ? "Change photo" : "Add photo (optional)"}
          <input type="file" accept="image/*" capture="environment" onChange={onPhoto} className="hidden" data-testid="prestart-photo-input" />
        </label>
        {photo && <img src={photo} alt="Pre-start" className="mt-3 max-h-40 border border-white/10 object-cover" data-testid="prestart-photo-preview" />}
      </div>

      {anyFail && (
        <div className="flex items-start gap-2 border border-[#FF3B30]/40 bg-[#FF3B30]/10 p-3 font-body text-sm text-[#FF3B30]" data-testid="prestart-fail-warning">
          <X className="w-4 h-4 mt-0.5 shrink-0" /> This machine will be flagged and the manager notified.
        </div>
      )}

      <div className="flex gap-3">
        <button className="btn-signal flex-1 h-12 font-head text-lg" disabled={loading} data-testid="prestart-submit">
          {loading ? <Loader2 className="w-5 h-5 animate-spin mx-auto" /> : "Submit Pre-Start"}
        </button>
        <button type="button" onClick={onCancel} className="h-12 px-5 border border-white/15 font-head text-[#94A3B8] hover:text-white transition-colors" data-testid="prestart-cancel">Cancel</button>
      </div>
    </form>
  );
}
